type Op =
  | { type: "equal"; text: string }
  | { type: "insert"; text: string }
  | { type: "delete"; text: string }
  | { type: "replace"; oldText: string; newText: string };

function sanitizeText(s: string) {
  // Only escape backticks within code blocks to prevent breaking out of code formatting
  return s.replace(/`/g, "\\`");
}

/**
 * Extract leading and trailing whitespace (including newlines) from text
 */
function extractSpaces(text: string): {
  leading: string;
  content: string;
  trailing: string;
} {
  const leadingMatch = text.match(/^[\s\r\n]*/);
  const trailingMatch = text.match(/[\s\r\n]*$/);

  const leading = leadingMatch ? leadingMatch[0] : "";
  const trailing = trailingMatch ? trailingMatch[0] : "";
  const content = text.slice(leading.length, text.length - trailing.length);

  return { leading, content, trailing };
}

function isOnlyWhitespace(text: string): boolean {
  return /^[ \t]*$/.test(text); // Only spaces and tabs, not newlines
}

function isOnlyNewlines(text: string): boolean {
  return /^[\r\n]+$/.test(text); // Only newlines
}

/**
 * Convert whitespace-only operations to equal operations to preserve spaces and line breaks
 * while avoiding highlighting them as changes
 */
function preserveWhitespaceOps(ops: Op[]): Op[] {
  return ops.map((op) => {
    if (op.type === "insert" || op.type === "delete") {
      // Always preserve newlines and regular whitespace without highlighting
      if (isOnlyWhitespace(op.text) || isOnlyNewlines(op.text)) {
        return { type: "equal" as const, text: op.text };
      }
    }
    return op;
  });
}

/**
 * Additional post-processing to prevent weird word breaks by merging
 * operations that don't have clear word boundaries between them
 */
function consolidateWordBoundaries(ops: Op[]): Op[] {
  const consolidated: Op[] = [];

  for (let i = 0; i < ops.length; i++) {
    const current = ops[i];
    const next = ops[i + 1];

    // If current and next are same type and neither ends/starts with word boundary,
    // merge them to prevent mid-word breaks
    if (next && current.type === next.type && current.type !== "replace") {
      const currentText =
        current.type === "equal"
          ? current.text
          : current.type === "insert"
            ? current.text
            : current.type === "delete"
              ? current.text
              : "";
      const nextText =
        next.type === "equal"
          ? next.text
          : next.type === "insert"
            ? next.text
            : next.type === "delete"
              ? next.text
              : "";

      // Check if we should merge (no word boundaries or newlines between them)
      if (
        !/\s$/.test(currentText) &&
        !/^\s/.test(nextText) &&
        !currentText.includes("\n") &&
        !nextText.includes("\n")
      ) {
        // Merge the operations
        if (current.type === "equal") {
          current.text += nextText;
        } else if (current.type === "insert") {
          current.text += nextText;
        } else if (current.type === "delete") {
          current.text += nextText;
        }
        i++; // Skip next since we merged it
      }
    }

    consolidated.push(current);
  }

  return consolidated;
}

/**
 * Tokenize more conservatively to avoid weird word breaks.
 * Keeps complete words, punctuation, line breaks, and whitespace as separate tokens.
 */
function tokenize(s: string): string[] {
  // Split preserving: newlines, spaces, words with hyphens/apostrophes, individual punctuation
  // This regex captures: newlines separately from other whitespace to preserve line structure
  const re =
    /(\r?\n|[ \t]+|[\p{L}\p{N}]+(?:['-][\p{L}\p{N}]+)*|[^\s\p{L}\p{N}])/gu;
  const m = s.match(re);
  return m ?? [];
}

/** Longest Common Subsequence DP table for arrays of strings */
function buildLcsTable(a: string[], b: string[]) {
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    Array(m + 1).fill(0),
  );
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (a[i - 1] === b[j - 1]) dp[i][j] = dp[i - 1][j - 1] + 1;
      else dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp;
}

/** Backtrack LCS table to produce token-level ops (insert/delete/equal) */
function backtrackLcs(a: string[], b: string[], dp: number[][]) {
  const ops: Op[] = [];
  let i = a.length;
  let j = b.length;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      ops.unshift({ type: "equal", text: a[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      ops.unshift({ type: "insert", text: b[j - 1] });
      j--;
    } else {
      ops.unshift({ type: "delete", text: a[i - 1] });
      i--;
    }
  }

  return ops;
}

/**
 * Post-process ops to group consecutive tokens of same op into single ops,
 * and pair consecutive delete+insert blocks into replace ops.
 */
function normalizeOps(rawOps: Op[]): Op[] {
  // First, collapse adjacent same-type ops except don't merge replace because they're not present yet
  const collapsed: Op[] = [];
  for (const op of rawOps) {
    const last = collapsed[collapsed.length - 1];
    if (last && last.type === op.type && op.type !== "replace") {
      // merge text
      if (op.type === "equal" || op.type === "insert" || op.type === "delete") {
        (last as any).text = (last as any).text + (op as any).text;
        continue;
      }
    }
    collapsed.push({ ...op });
  }

  // Now merge consecutive delete+insert pairs into single replace operations
  // Also merge pairs that are separated by whitespace-only equal operations
  const result: Op[] = [];
  let idx = 0;
  while (idx < collapsed.length) {
    const cur = collapsed[idx];

    // Check if we have a delete+insert pair
    if (
      cur.type === "delete" &&
      idx + 1 < collapsed.length &&
      collapsed[idx + 1].type === "insert"
    ) {
      // Collect all consecutive delete+insert pairs, including whitespace between them
      const deleteTexts: string[] = [(cur as any).text];
      const insertTexts: string[] = [(collapsed[idx + 1] as any).text];
      idx += 2;

      // Continue collecting while we have alternating delete+insert pairs
      // Also merge whitespace-only equal operations that appear between pairs
      while (idx < collapsed.length) {
        // Check if next is whitespace-only equal followed by delete+insert
        if (
          collapsed[idx].type === "equal" &&
          isOnlyWhitespace((collapsed[idx] as any).text) &&
          idx + 1 < collapsed.length &&
          collapsed[idx + 1].type === "delete" &&
          idx + 2 < collapsed.length &&
          collapsed[idx + 2].type === "insert"
        ) {
          // Include the whitespace in both old and new text
          const whitespace = (collapsed[idx] as any).text;
          deleteTexts.push(whitespace + (collapsed[idx + 1] as any).text);
          insertTexts.push(whitespace + (collapsed[idx + 2] as any).text);
          idx += 3;
        }
        // Check if next is a direct delete+insert pair
        else if (
          collapsed[idx].type === "delete" &&
          idx + 1 < collapsed.length &&
          collapsed[idx + 1].type === "insert"
        ) {
          deleteTexts.push((collapsed[idx] as any).text);
          insertTexts.push((collapsed[idx + 1] as any).text);
          idx += 2;
        } else {
          // No more consecutive pairs to merge
          break;
        }
      }

      // Merge all collected deletes and inserts into one replace operation
      const oldText = deleteTexts.join("");
      const newText = insertTexts.join("");
      result.push({ type: "replace", oldText, newText });
    } else {
      result.push(cur);
      idx++;
    }
  }

  return result;
}

/**
 * Normalize spacing around punctuation for cleaner matches while preserving line breaks
 */
function normalizeSpacing(text: string): string {
  return text
    .replace(/\s*\(\s*/g, " (") // normalize spaces around opening parentheses
    .replace(/\s*\)\s*/g, ") ") // normalize spaces around closing parentheses
    .replace(/\s*,\s*/g, ", ") // normalize spaces around commas
    .replace(/\s*\.\s*/g, ". ") // normalize spaces around periods
    .replace(/[ \t]+/g, " ") // collapse multiple spaces/tabs but preserve newlines
    .replace(/[ \t]*\n[ \t]*/g, "\n") // normalize spaces around newlines
    .trim();
}

/**
 * Split operations that contain line breaks into separate operations.
 * This preserves line breaks as separate equal operations that won't be stripped.
 */
function splitOpsAtLineBreaks(ops: Op[]): Op[] {
  const result: Op[] = [];

  for (const op of ops) {
    if (op.type === "insert" && /\r?\n/.test(op.text)) {
      // Split insert operation at line breaks
      const lines = op.text.split(/(\r?\n)/);
      for (let i = 0; i < lines.length; i++) {
        if (/\r?\n/.test(lines[i])) {
          // This is a newline - add as equal operation
          result.push({ type: "equal", text: lines[i] });
        } else if (lines[i].length > 0) {
          // This is content - add as insert operation
          result.push({ type: "insert", text: lines[i] });
        }
      }
    } else if (
      op.type === "replace" &&
      (/\r?\n/.test(op.oldText) || /\r?\n/.test(op.newText))
    ) {
      // Split replace operation at line breaks
      // Split both texts, preserving newlines in the split
      const oldParts = op.oldText.split(/(\r?\n)/);
      const newParts = op.newText.split(/(\r?\n)/);

      // Process parts, creating operations for each segment
      let oldIdx = 0;
      let newIdx = 0;

      while (oldIdx < oldParts.length || newIdx < newParts.length) {
        const oldPart = oldParts[oldIdx] || "";
        const newPart = newParts[newIdx] || "";

        // Check if current parts are newlines
        const oldIsNewline = /\r?\n/.test(oldPart);
        const newIsNewline = /\r?\n/.test(newPart);

        if (oldIsNewline && newIsNewline) {
          // Both are newlines - add as equal (use oldPart, they should be the same)
          result.push({ type: "equal", text: oldPart });
          oldIdx++;
          newIdx++;
        } else if (oldIsNewline) {
          // Old is newline, new is content - add newline as equal, then insert new content
          result.push({ type: "equal", text: oldPart });
          oldIdx++;
          if (newPart) {
            result.push({ type: "insert", text: newPart });
            newIdx++;
          }
        } else if (newIsNewline) {
          // New is newline, old is content - delete old content, then add newline as equal
          if (oldPart) {
            result.push({ type: "delete", text: oldPart });
            oldIdx++;
          }
          result.push({ type: "equal", text: newPart });
          newIdx++;
        } else {
          // Both are content - create replace operation
          if (oldPart && newPart) {
            result.push({
              type: "replace",
              oldText: oldPart,
              newText: newPart,
            });
          } else if (oldPart) {
            result.push({ type: "delete", text: oldPart });
          } else if (newPart) {
            result.push({ type: "insert", text: newPart });
          }
          oldIdx++;
          newIdx++;
        }
      }
    } else {
      // No line breaks, keep as is
      result.push(op);
    }
  }

  return result;
}

/**
 * Format code content - use markdown backticks for inline code
 * (we no longer need HTML code tags since we split at line breaks)
 */
function formatCodeContent(content: string): string {
  const sanitized = sanitizeText(content);
  // Always use markdown backticks since line breaks are handled separately
  return `\`${sanitized}\``;
}

/**
 * Main: produce Markdown showing diff (only additions and deletions).
 * Options:
 *  - inlineDeletes: whether to show deletions inline (default true)
 */
export function diffToMarkdown(
  original: string,
  modified: string,
  opts?: { inlineDeletes?: boolean },
): string {
  const inlineDeletes = opts?.inlineDeletes ?? true;

  // Normalize spacing for cleaner matches
  const normalizedOriginal = normalizeSpacing(original);
  const normalizedModified = normalizeSpacing(modified);

  const aTokens = tokenize(normalizedOriginal);
  const bTokens = tokenize(normalizedModified);
  const dp = buildLcsTable(aTokens, bTokens);
  const rawOps = backtrackLcs(aTokens, bTokens, dp);
  const normalizedOps = normalizeOps(rawOps);
  const splitOps = splitOpsAtLineBreaks(normalizedOps);
  const consolidatedOps = consolidateWordBoundaries(splitOps);
  const ops = preserveWhitespaceOps(consolidatedOps);

  const parts: string[] = [];

  for (const op of ops) {
    if (op.type === "equal") {
      parts.push(op.text);
    } else if (op.type === "insert") {
      // Addition - show as code but keep spaces outside to preserve text flow
      const { leading, content, trailing } = extractSpaces(op.text);
      if (content.length > 0) {
        // Preserve leading/trailing spaces outside the code formatting
        parts.push(leading + formatCodeContent(content) + trailing);
      } else {
        // If only whitespace, add it as normal text (should be rare due to preserveWhitespaceOps)
        parts.push(op.text);
      }
    } else if (op.type === "delete") {
      // Deletion - show as strikethrough if allowed
      if (inlineDeletes) {
        parts.push(`<s>${op.text}</s>`);
      }
    } else if (op.type === "replace") {
      // Extract only leading/trailing whitespace - internal whitespace stays in formatting
      const oldSpaces = extractSpaces(op.oldText);
      const newSpaces = extractSpaces(op.newText);

      // Render: leading whitespace + formatted content + trailing whitespace
      // Internal whitespace is preserved within the formatted content
      const leading = oldSpaces.leading || newSpaces.leading || "";
      const trailing = oldSpaces.trailing || newSpaces.trailing || "";

      // Add leading whitespace before the formatting
      if (leading) {
        parts.push(leading);
      }

      if (inlineDeletes && oldSpaces.content) {
        parts.push(`<s>${oldSpaces.content}</s>`);
      }
      if (newSpaces.content) {
        parts.push(formatCodeContent(newSpaces.content));
      }

      // Add trailing whitespace after the formatting
      if (trailing) {
        parts.push(trailing);
      }
    }
  }

  return parts.join("");
}
